"use client";

import Image from "next/image";
import { Icon, type IconName } from "@/components/ui/icon";
import { SyncDirectionIcon } from "@/features/ats-sync/components/sync-direction-icon";
import { useJobAtsLink } from "@/features/ats-sync/hooks/use-job-ats-link";
import { usePipelineJobOptional } from "@/features/jobs/context/pipeline-job-context";
import { useJobConnectorLogo } from "@/features/jobs/hooks/use-connector-logo";

export function HeaderLabel({ icon, label }: { icon: IconName; label: string }) {
  return (
    <span className="flex items-center gap-1 min-w-0">
      <Icon name={icon} size={14} className="text-muted-foreground shrink-0" />
      <span className="truncate" title={label}>{label}</span>
    </span>
  );
}

/**
 * ATS column header — shows the job's connected ATS logo instead of a generic
 * icon, falling back to the live sync-direction glyph until the logo resolves
 * (or when the job has no logo on file).
 */
export function AtsHeaderLabel({ label = "ATS" }: { label?: string }) {
  const jobCtx = usePipelineJobOptional();
  const jobId = jobCtx?.job.id ?? null;
  const logo = useJobConnectorLogo(jobId);
  const { syncDirection } = useJobAtsLink(jobId);
  return (
    <span className="flex items-center gap-1 min-w-0">
      {logo ? (
        <Image
          src={logo}
          alt=""
          width={14}
          height={14}
          className="size-3.5 shrink-0 rounded-[3px] object-cover"
          unoptimized
        />
      ) : (
        <SyncDirectionIcon direction={syncDirection} />
      )}
      <span className="truncate" title={label}>{label}</span>
    </span>
  );
}
